import queryString from "query-string";

import { axios } from "../api";

import type { AeronetV6Response } from "../types";
import type {
  AddLink,
  AircraftLinkedFiles,
  Attachment,
  AttachmentCreate,
  FinalizeUpload,
  FolderListing,
  RecordFile,
} from "./types";

/** The server's answer on a tenant without version-37 (app/attachments/objects.py unavailable()):
 *  a 503 with a TOP-LEVEL `code`. A 503 without it is a real storage failure, not this. */
export function objectsUnavailable(e: unknown): boolean {
  const r = (e as { response?: { status?: number; data?: unknown } })?.response;
  return r?.status === 503 && (r?.data as { code?: unknown } | undefined)?.code === "OBJECTS_UNAVAILABLE";
}

class _AttachmentService {
  public async create(data: AttachmentCreate): Promise<Attachment> {
    const resp = await axios.post("/v6/attachments", data);
    return resp.data;
  }

  async _query(url: string, filter: string[]): Promise<readonly Attachment[]> {
    const params = { filter };
    const resp = await axios.get<AeronetV6Response<Attachment>>(url, {
      params,
      paramsSerializer: (params) => queryString.stringify(params),
    });
    return resp.data.data;
  }

  public async query(filter: string[]): Promise<readonly Attachment[]> {
    return this._query("/v6/attachments", filter);
  }

  public async queryResource(
    source: string,
    id: number,
    filter: string[]
  ): Promise<readonly Attachment[]> {
    return this._query(`/v6/attachments/${source}/${id}`, filter);
  }

  public async delete(id: number): Promise<void> {
    return await axios.delete(`/v6/attachments/${id}`);
  }

  public async deleteAll(source: string, id: number): Promise<void> {
    return await axios.delete(`/v6/attachments/${source}/${id}`);
  }

  /**
   * Delete this record's attachment row(s) for one stored file, found by its storage
   * key server-side (H282) — including rows written before H282, which kept the
   * picked name while the key kept the sanitised one.
   */
  public async deleteForKey(source: string, id: number, key: string): Promise<number> {
    const resp = await axios.delete<{ deleted: number }>(`/v6/attachments/${source}/${id}/file`, {
      params: { key },
    });
    return resp.data.deleted;
  }

  /** Every live file on a record. The list route pages (at most 100 a page), so this reads
   *  until `total`: a record with 120 files must not show 50. The route has no default ORDER BY,
   *  so LIMIT/OFFSET paging could repeat or skip rows: `sort: "id"` (the backend's comma-separated
   *  "-field" form, app/sorting.py get_sort; ascending, and not a guarded column, so
   *  objects.refuse_guarded_specs accepts it) makes the pages stable. Uncached: the Files tab
   *  re-reads after every upload/remove and must not see axios-cache-interceptor's stale copy. */
  public async listForRecord(source: string, id: number): Promise<RecordFile[]> {
    const rows: RecordFile[] = [];
    for (let pageNo = 1; ; pageNo++) {
      const resp = await axios.get<AeronetV6Response<RecordFile>>(`/v6/attachments/${source}/${id}`, {
        params: { page: pageNo, size: 100, sort: "id" },
        cache: false,
      });
      const page = resp.data.data;
      rows.push(...page);
      // A short page is the last; the page count bounds the loop even if a server repeated pages.
      if (page.length < 100 || pageNo * 100 >= resp.data.total) return rows;
    }
  }

  /** The aircraft Files tab's roll-up: the files on its work orders and ADs/SBs, grouped by
   *  record (read-only; app/attachments/rollup.py). Uncached, like the record list beside it. */
  public async aircraftLinked(aircraftId: number): Promise<AircraftLinkedFiles> {
    const resp = await axios.get<AircraftLinkedFiles>(`/v6/attachments/aircraft/${aircraftId}/linked`, {
      cache: false,
    });
    return resp.data;
  }

  /** The live folder listing (P10): bytes no row of this record claims, plus where each row's
   *  bytes are. The caller treats a failure as non-fatal, so no global toast. Uncached. */
  public async unregistered(source: string, id: number, prefix: string): Promise<FolderListing> {
    const resp = await axios.get<FolderListing>(`/v6/attachments/${source}/${id}/unregistered`, {
      params: { prefix },
      cache: false,
      suppressErrorToast: true,
    });
    return resp.data;
  }

  /** After the PUT to the signed URL: the file and its usage in one server commit. The caller
   *  shows its own messages (and falls back on `objectsUnavailable`). */
  public async finalize(data: FinalizeUpload): Promise<RecordFile> {
    const resp = await axios.post("/v6/attachments/finalize", data, { suppressErrorToast: true });
    return resp.data;
  }

  public async addLink(data: AddLink): Promise<RecordFile> {
    const resp = await axios.post("/v6/attachments/link", data, { suppressErrorToast: true });
    return resp.data;
  }

  /** Take the file off this record only (soft, idempotent); the file itself stays. */
  public async remove(id: number): Promise<void> {
    await axios.post(`/v6/attachments/${id}/remove`, undefined, { suppressErrorToast: true });
  }
}

export let AttachmentService = new _AttachmentService();
